import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, Trash2, Layers } from 'lucide-react';
import { Container, PageHeader } from '@vnetwork/ui/layout';
import {
  Button,
  IconButton,
  Input,
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from '@vnetwork/ui/primitives';
import { Field } from '@vnetwork/ui/forms';
import { useToast } from '@vnetwork/ui/feedback';
import { useCreatePresetGroup } from '@/api/media/vod-presets';
import type { CustomPresetSpec } from '@/api/media/vod-presets-types';
import { presetProfiles } from '@/pages/services/shared/transcoding-preset-constants';
import { PresetProfileCard } from '@/pages/services/shared/PresetProfileCard';
import styles from './PresetGroups.module.css';

// ─── Custom preset spec helpers ───────────────────────────────────────────────

const DEFAULT_CUSTOM_SPEC: CustomPresetSpec = {
  name: '',
  description: '',
  video: { codec: 'libx264', bitrate: 1000, fps: 30, gop: 60, profile: 'main', kar: 0, width: 1280, height: 720 },
  audio: { codec: 'libfdk_aac', bitrate: 128, sample_rate: 44100, channels: 2 },
};

type CustomSpecErrors = { name?: string; fps?: string; dimensions?: string };

// Each custom preset entry carries a stable UUID so that removing a middle
// card does not shift error-map indices or cause stale error display.
interface CustomSpecEntry {
  id: string;
  spec: CustomPresetSpec;
}

function validateCustomSpec(spec: CustomPresetSpec): CustomSpecErrors {
  const errs: CustomSpecErrors = {};
  if (!spec.name || !/^[a-zA-Z0-9_]{1,40}$/.test(spec.name)) {
    errs.name = 'Only [a-z, A-Z, 0-9, _], max 40 characters.';
  }
  if (spec.video.fps > 60) errs.fps = 'FPS must be ≤ 60.';
  if (spec.video.kar === 1) {
    const w = Number(spec.video.width) || 0;
    const h = Number(spec.video.height) || 0;
    if (w > 0 && h > 0) errs.dimensions = 'With keep-aspect-ratio, specify only Width OR Height, not both.';
    else if (w === 0 && h === 0) errs.dimensions = 'With keep-aspect-ratio, specify either Width or Height.';
  }
  return errs;
}

// ─── Custom spec card ─────────────────────────────────────────────────────────

function CustomSpecCard({
  entry,
  index,
  errors,
  onChange,
  onRemove,
}: {
  entry: CustomSpecEntry;
  index: number;
  errors?: CustomSpecErrors;
  onChange: (id: string, spec: CustomPresetSpec) => void;
  onRemove: (id: string) => void;
}) {
  const { id, spec } = entry;
  const setVideo = (key: keyof CustomPresetSpec['video'], value: string | number) =>
    onChange(id, { ...spec, video: { ...spec.video, [key]: value } });
  const setAudio = (key: keyof CustomPresetSpec['audio'], value: string | number) =>
    onChange(id, { ...spec, audio: { ...spec.audio, [key]: value } });

  return (
    <div className={styles.customSpecCard}>
      <div className={styles.customSpecHeader}>
        <span className={styles.customSpecTitle}>Custom Preset #{index + 1}</span>
        <IconButton size="sm" variant="danger" aria-label="Remove custom preset" onClick={() => onRemove(id)}>
          <Trash2 size={13} />
        </IconButton>
      </div>

      <div className={styles.grid2}>
        <Field label="Name" required error={errors?.name}>
          {(p) => (
            <Input {...p} value={spec.name} placeholder="MyPreset_1080P"
              onChange={(e) => onChange(id, { ...spec, name: e.target.value })} invalid={!!errors?.name} />
          )}
        </Field>
        <Field label="Description">
          {(p) => (
            <Input {...p} value={spec.description} placeholder="Optional description"
              onChange={(e) => onChange(id, { ...spec, description: e.target.value })} />
          )}
        </Field>
      </div>

      <p className={styles.customSpecSubtitle}>Video</p>
      <div className={styles.grid3}>
        <Field label="Codec">
          {(p) => (
            <Select value={spec.video.codec} onValueChange={(v) => setVideo('codec', v)}>
              <SelectTrigger {...p}><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="libx264">libx264 (H.264)</SelectItem>
                <SelectItem value="libx265">libx265 (H.265)</SelectItem>
              </SelectContent>
            </Select>
          )}
        </Field>
        <Field label="Bitrate (kbps)">
          {(p) => (
            <Input {...p} type="number" value={spec.video.bitrate}
              onChange={(e) => setVideo('bitrate', Number(e.target.value))} />
          )}
        </Field>
        <Field label="FPS" error={errors?.fps}>
          {(p) => (
            <Input {...p} type="number" value={spec.video.fps} invalid={!!errors?.fps}
              onChange={(e) => setVideo('fps', Number(e.target.value))} />
          )}
        </Field>
        <Field label="GOP">
          {(p) => (
            <Input {...p} type="number" value={spec.video.gop}
              onChange={(e) => setVideo('gop', Number(e.target.value))} />
          )}
        </Field>
        <Field label="Profile">
          {(p) => (
            <Select value={spec.video.profile} onValueChange={(v) => setVideo('profile', v)}>
              <SelectTrigger {...p}><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="baseline">Baseline</SelectItem>
                <SelectItem value="main">Main</SelectItem>
                <SelectItem value="high">High</SelectItem>
              </SelectContent>
            </Select>
          )}
        </Field>
        <Field label="Keep Aspect Ratio">
          {(p) => (
            <Select value={String(spec.video.kar)} onValueChange={(v) => setVideo('kar', Number(v))}>
              <SelectTrigger {...p}><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="0">No</SelectItem>
                <SelectItem value="1">Yes</SelectItem>
              </SelectContent>
            </Select>
          )}
        </Field>
        <Field label="Width (px)" error={errors?.dimensions}>
          {(p) => (
            <Input {...p} type="number" value={spec.video.width ?? ''}
              invalid={!!errors?.dimensions}
              onChange={(e) => setVideo('width', Number(e.target.value))} />
          )}
        </Field>
        <Field label="Height (px)">
          {(p) => (
            <Input {...p} type="number" value={spec.video.height ?? ''}
              onChange={(e) => setVideo('height', Number(e.target.value))} />
          )}
        </Field>
      </div>

      <p className={styles.customSpecSubtitle}>Audio</p>
      <div className={styles.grid3}>
        <Field label="Codec">
          {(p) => (
            <Select value={spec.audio.codec} onValueChange={(v) => setAudio('codec', v)}>
              <SelectTrigger {...p}><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="libfdk_aac">libfdk_aac (AAC)</SelectItem>
              </SelectContent>
            </Select>
          )}
        </Field>
        <Field label="Bitrate (kbps)">
          {(p) => (
            <Input {...p} type="number" value={spec.audio.bitrate}
              onChange={(e) => setAudio('bitrate', Number(e.target.value))} />
          )}
        </Field>
        <Field label="Sample Rate (Hz)">
          {(p) => (
            <Select value={String(spec.audio.sample_rate)} onValueChange={(v) => setAudio('sample_rate', Number(v))}>
              <SelectTrigger {...p}><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="48000">48000 Hz</SelectItem>
                <SelectItem value="44100">44100 Hz</SelectItem>
                <SelectItem value="22050">22050 Hz</SelectItem>
                <SelectItem value="11025">11025 Hz</SelectItem>
                <SelectItem value="5512">5512 Hz</SelectItem>
              </SelectContent>
            </Select>
          )}
        </Field>
        <Field label="Channels">
          {(p) => (
            <Select value={String(spec.audio.channels)} onValueChange={(v) => setAudio('channels', Number(v))}>
              <SelectTrigger {...p}><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="2">2 (Stereo)</SelectItem>
                <SelectItem value="1">1 (Mono)</SelectItem>
              </SelectContent>
            </Select>
          )}
        </Field>
      </div>
    </div>
  );
}

// ─── Main create component ────────────────────────────────────────────────────

export function VodPresetGroupCreate({ serviceId }: { serviceId: string }) {
  const navigate = useNavigate();
  const toast = useToast();

  const [name, setName] = useState('');
  const [desc, setDesc] = useState('');
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());
  const [customSpecs, setCustomSpecs] = useState<CustomSpecEntry[]>([]);
  const [formError, setFormError] = useState<string | null>(null);
  const [customErrors, setCustomErrors] = useState<Record<string, CustomSpecErrors>>({});

  const createMutation = useCreatePresetGroup(serviceId);

  const toggleProfile = (id: number) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
    setFormError(null);
  };

  const addCustomSpec = () =>
    setCustomSpecs((prev) => [
      ...prev,
      {
        id: crypto.randomUUID(),
        spec: { ...DEFAULT_CUSTOM_SPEC, video: { ...DEFAULT_CUSTOM_SPEC.video }, audio: { ...DEFAULT_CUSTOM_SPEC.audio } },
      },
    ]);

  const updateCustomSpec = (id: string, spec: CustomPresetSpec) => {
    setCustomSpecs((prev) => prev.map((e) => (e.id === id ? { ...e, spec } : e)));
    setCustomErrors((prev) => { const n = { ...prev }; delete n[id]; return n; });
  };

  const removeCustomSpec = (id: string) => {
    setCustomSpecs((prev) => prev.filter((e) => e.id !== id));
    setCustomErrors((prev) => { const n = { ...prev }; delete n[id]; return n; });
  };

  const validate = (): boolean => {
    if (!name.trim()) { setFormError('Name is required.'); return false; }
    if (selectedIds.size === 0 && customSpecs.length === 0) {
      setFormError('Select at least one profile or add a custom preset.'); return false;
    }
    const specErrs: Record<string, CustomSpecErrors> = {};
    let hasErrs = false;
    customSpecs.forEach(({ id, spec }) => {
      const errs = validateCustomSpec(spec);
      if (Object.keys(errs).length) { specErrs[id] = errs; hasErrs = true; }
    });
    if (hasErrs) { setCustomErrors(specErrs); setFormError('Fix custom preset errors before saving.'); return false; }
    setFormError(null);
    return true;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!validate()) return;
    // When kar=1, omit falsy width/height — the server rejects zero-dimensions.
    const formattedSpecs = customSpecs.map(({ spec }) => {
      const newSpec = { ...spec, video: { ...spec.video } };
      if (newSpec.video.kar === 1) {
        if (!newSpec.video.width) delete newSpec.video.width;
        if (!newSpec.video.height) delete newSpec.video.height;
      }
      return newSpec;
    });

    createMutation.mutate(
      {
        name: name.trim(),
        desc: desc.trim(),
        preset_ids: Array.from(selectedIds).map(String),
        ...(formattedSpecs.length ? { custom_preset_spec: formattedSpecs } : {}),
      },
      {
        onSuccess: () => { toast.success('Preset group created'); navigate(`/services/vod/${serviceId}/transcoding-presets`); },
        onError: (err) => setFormError((err as Error).message || 'Failed to create preset group.'),
      },
    );
  };

  const back = () => navigate(`/services/vod/${serviceId}/transcoding-presets`);

  return (
    <Container size="lg">
      <PageHeader
        icon={<Layers size={22} />}
        title="Create Preset Group"
        description="Create a named group of quality profiles for transcoding jobs."
      />
      <form className={styles.page} onSubmit={handleSubmit}>
        {formError && <div className={styles.errorBanner}>{formError}</div>}

        {/* Basic info */}
        <div className={styles.card}>
          <h3 className={styles.cardTitle}>Group Info</h3>
          <div className={styles.grid2}>
            <Field label="Name" required>
              {(p) => (
                <Input {...p} placeholder="e.g. Mobile ABR" value={name}
                  onChange={(e) => setName(e.target.value)} invalid={!!formError && !name.trim()} />
              )}
            </Field>
            <Field label="Description">
              {(p) => <Input {...p} placeholder="Optional description" value={desc} onChange={(e) => setDesc(e.target.value)} />}
            </Field>
          </div>
        </div>

        {/* Predefined profiles */}
        <div className={styles.card}>
          <h3 className={styles.cardTitle}>Predefined Profiles</h3>
          <p className={styles.cardDesc}>Select one or more encoding profiles to include in this group.</p>
          {selectedIds.size === 0 && customSpecs.length === 0 && formError && (
            <p className={styles.selectionError}>{formError}</p>
          )}
          <div className={styles.profileGrid}>
            {presetProfiles.map((profile) => (
              <PresetProfileCard
                key={profile.id}
                profile={profile}
                selected={selectedIds.has(profile.id)}
                onToggle={() => toggleProfile(profile.id)}
              />
            ))}
          </div>
        </div>

        {/* Custom presets */}
        <div className={styles.card}>
          <h3 className={styles.cardTitle}>Custom Presets</h3>
          <p className={styles.cardDesc}>Define custom codec/bitrate specs for advanced encoding requirements.</p>

          {customSpecs.map((entry, i) => (
            <CustomSpecCard
              key={entry.id}
              entry={entry}
              index={i}
              errors={customErrors[entry.id]}
              onChange={updateCustomSpec}
              onRemove={removeCustomSpec}
            />
          ))}

          <Button
            type="button"
            variant="secondary"
            size="sm"
            startIcon={<Plus size={13} />}
            className={styles.addSpecButton}
            onClick={addCustomSpec}
          >
            Add Custom Preset
          </Button>
        </div>

        <div className={styles.actions}>
          <Button variant="secondary" type="button" onClick={back} disabled={createMutation.isPending}>
            Cancel
          </Button>
          <Button variant="primary" type="submit" disabled={createMutation.isPending}>
            {createMutation.isPending ? 'Creating…' : 'Create Preset Group'}
          </Button>
        </div>
      </form>
    </Container>
  );
}
